import { Link, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/react';
import { useInspectorProfile } from '@/contexts/InspectorProfileContext';
import type { User } from '@/types';

type NavItem = { path: string; label: string; icon: IconName };

type IconName = 'home' | 'clipboard' | 'calendar' | 'users' | 'book' | 'file-text' | 'library' | 'chart' | 'gear' | 'message';

// Contacts and Analytics are hidden until those surfaces are more polished.
// Routes still resolve so direct URLs and dev work keep working.
const NAV_ITEMS: NavItem[] = [
  { path: '/inspections', label: 'Inspections', icon: 'clipboard' },
  { path: '/reports', label: 'Reports', icon: 'book' },
  { path: '/scheduling', label: 'Scheduling', icon: 'calendar' },
  { path: '/templates', label: 'Templates', icon: 'file-text' },
  { path: '/library', label: 'Library', icon: 'library' },
  { path: '/settings', label: 'Settings', icon: 'gear' },
];

const ICON_PATHS: Record<IconName, string> = {
  home: 'M3 12 12 3l9 9M5 10v10h14V10',
  clipboard: 'M9 3h6v4H9zM6 5h3M15 5h3v15H6V5h3',
  calendar: 'M3 6h18v15H3zM3 10h18M8 3v4M16 3v4',
  users: 'M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M2 20v-1a6 6 0 0 1 12 0v1M16 11a4 4 0 1 0 0-8M22 20v-1a6 6 0 0 0-6-6',
  book: 'M4 4h14a2 2 0 0 1 2 2v14H6a2 2 0 0 1-2-2zM4 20a2 2 0 0 1 2-2h14',
  'file-text': 'M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2zM14 2v6h6M16 13H8M16 17H8M10 9H8',
  library: 'M4 6v14M8 6v14M13 4l-2.2 15.5 4 .6L17 4.6zM13 4l4 .6',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 14.4a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8L4 7a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h0a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8h0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z',
  message: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
};

function NavIcon({ name }: { name: IconName }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

function formatRole(role: string | null | undefined): string {
  if (!role) return 'Inspector';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function formatOrgSub(profile: User | null): string {
  if (!profile) return '';
  const parts = [profile.businessEntityType, profile.businessLocation].filter(
    (s): s is string => Boolean(s && s.trim()),
  );
  if (parts.length > 0) return parts.join(' · ');
  return formatRole(profile.role);
}

export function Sidebar() {
  const location = useLocation();
  const { user: clerkUser } = useUser();
  const { profile } = useInspectorProfile();

  const companyName =
    profile?.companyName ??
    (clerkUser?.publicMetadata?.companyName as string | undefined) ??
    clerkUser?.fullName ??
    'Your Company';
  const initial = (companyName || 'F').charAt(0).toUpperCase();
  const orgSub = formatOrgSub(profile);

  return (
    <aside className="iw-sidebar">
      <nav className="iw-nav" aria-label="Primary">
        {NAV_ITEMS.map((item) => {
          const active = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={'iw-nav-item' + (active ? ' iw-nav-item--on' : '')}
            >
              <NavIcon name={item.icon} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="iw-sidebar-foot">
        <a
          href="mailto:beta@flowspect.app?subject=Flowspect%20feedback"
          className="iw-nav-item"
          style={{ marginBottom: 8 }}
        >
          <NavIcon name="message" />
          <span>Send feedback</span>
        </a>
        <div className="iw-org">
          <div className="iw-avatar iw-avatar--md">{initial}</div>
          <div className="iw-org-text">
            <div className="iw-org-name">{companyName}</div>
            <div className="iw-org-sub">{orgSub}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
